/**
 * YNS Education Hub - Main Orchestrator
 * Coordinates all hub modules and manages global state.
 *
 * SECURITY NOTE:
 * Video data (Vimeo IDs) is now server-side rendered via Locksmith.
 * The YNS_SECURE_DATA object only contains Vimeo IDs for users who have
 * purchased access. Unauthorized users never receive video IDs in their
 * page source - there's nothing to bypass.
 *
 * This is the main entry point that initializes and coordinates:
 * - Tab routing
 * - Video library (using secure data from Locksmith)
 * - PDF library
 * - Study mode
 * - Streak system
 * - Activity feed
 */

(function() {
  'use strict';

  // ============================================
  // Configuration
  // ============================================
  const CONFIG = {
    // Supabase configuration (same as video library)
    supabase: window.YNS_SUPABASE_CONFIG || {
      url: '',
      anonKey: ''
    },
    storageKeys: {
      videoProgress: 'yns_video_progress',
      thumbnailCache: 'yns_vimeo_thumbnails'
    }
  };

  // Ensure tableName is set (matches yns-video-library.js)
  CONFIG.supabase.tableName = CONFIG.supabase.tableName || 'yns_locksmith_video_progress';

  // ============================================
  // Video Data - Now loaded from secure server data
  // ============================================
  // SECURITY: Video IDs are now server-side rendered via Locksmith.
  // The YNS_SECURE_DATA.videos array only contains Vimeo IDs for
  // users who have purchased access. This cannot be bypassed.

  let VIDEOS = [];

  /**
   * Initialize videos from secure server-rendered data.
   * This must be called before any video functionality is used.
   */
  function initVideosFromSecureData() {
    const secureData = window.YNS_SECURE_DATA;

    if (secureData && secureData.videos && Array.isArray(secureData.videos)) {
      VIDEOS = secureData.videos.map(video => ({
        id: video.id,
        vimeoId: video.vimeoId || null, // Only present if user has access
        vimeoHash: video.vimeoHash || null, // Private-link code for unlisted Vimeo videos
        title: video.title,
        duration: video.duration,
        category: video.category,
        lesson: video.lesson,
        xp: video.xp,
        hasAccess: video.hasAccess || false,
        purchaseUrl: video.purchaseUrl || '/products/marks-2026-nclex-review-mega-bundle-video-lectures-pdf-study-guides-professor-mark-johnsons-nclex-mega-bundle'
      }));

    } else {
      console.warn('YNS_SECURE_DATA.videos not available');
      VIDEOS = [];
    }

    // Make VIDEOS available globally for other modules
    window.YNS_VIDEO_DATA = VIDEOS;
  }

  /**
   * Check if user has access to a specific video.
   * Returns true only if Locksmith granted access (vimeoId is present).
   */
  function hasVideoAccess(videoId) {
    const video = VIDEOS.find(v => v.id === videoId);
    return video && video.hasAccess && video.vimeoId;
  }

  /**
   * Get the Vimeo ID for a video (only if user has access).
   * Returns null if user doesn't have access.
   */
  function getSecureVimeoId(videoId) {
    const video = VIDEOS.find(v => v.id === videoId);
    if (video && video.hasAccess && video.vimeoId) {
      return video.vimeoId;
    }
    return null;
  }

  // ============================================
  // Level System
  // ============================================
  const LEVELS = [
    { level: 1, xpRequired: 0, title: 'Beginner' },
    { level: 2, xpRequired: 250, title: 'Student' },
    { level: 3, xpRequired: 500, title: 'Apprentice' },
    { level: 4, xpRequired: 1000, title: 'Practitioner' },
    { level: 5, xpRequired: 1500, title: 'Scholar' },
    { level: 6, xpRequired: 2000, title: 'Expert' },
    { level: 7, xpRequired: 2500, title: 'Master' },
    { level: 8, xpRequired: 3000, title: 'Champion' }
  ];

  // ============================================
  // State
  // ============================================
  let currentVideoIndex = 0;
  let currentCategory = 'all';
  let currentSort = 'recommended';
  let syncTimeout = null;
  let currentPlayer = null;
  let currentVideoId = null;
  let lastSaveTime = 0;

  // ============================================
  // Progress Management
  // ============================================
  function getProgress() {
    try {
      const stored = localStorage.getItem(CONFIG.storageKeys.videoProgress);
      return stored ? JSON.parse(stored) : {};
    } catch (e) {
      return {};
    }
  }

  function saveProgress(progress) {
    try {
      localStorage.setItem(CONFIG.storageKeys.videoProgress, JSON.stringify(progress));
      scheduleSync();
    } catch (e) {
      console.error('Error saving progress:', e);
    }
  }

  function getVideoProgress(videoId) {
    const progress = getProgress();
    return progress[videoId] || {
      currentTime: 0,
      duration: 0,
      progress: 0,
      watched: 0,
      completed: false,
      lastWatchedAt: null
    };
  }

  function updateVideoProgress(videoId, data) {
    const progress = getProgress();
    const existing = getVideoProgress(videoId);
    progress[videoId] = { ...existing, ...data, lastWatchedAt: new Date().toISOString() };
    saveProgress(progress);
    renderVideos();
    updateStats();
  }

  // ============================================
  // Server Sync
  // ============================================
  function scheduleSync() {
    if (syncTimeout) clearTimeout(syncTimeout);
    syncTimeout = setTimeout(syncProgressToServer, 2000);
  }

  async function syncProgressToServer() {
    const config = window.YNS_CONFIG || {};
    const customerId = config.customerId;
    if (!customerId) return;

    const progress = getProgress();

    for (const [videoId, data] of Object.entries(progress)) {
      try {
        await fetch(`${CONFIG.supabase.url}/rest/v1/${CONFIG.supabase.tableName}?on_conflict=customer_id,video_id`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': CONFIG.supabase.anonKey,
            'Authorization': `Bearer ${CONFIG.supabase.anonKey}`,
            'Prefer': 'resolution=merge-duplicates'
          },
          body: JSON.stringify({
            customer_id: String(customerId),
            video_id: String(videoId),
            current_time_seconds: Math.floor(data.currentTime || 0),
            duration_seconds: Math.floor(data.duration || 0),
            progress_percent: Math.floor(data.progress || data.watched || 0),
            is_completed: data.completed || false,
            completed_at: data.completed ? new Date().toISOString() : null
          })
        });
      } catch (e) {
        console.error('Sync error:', e);
      }
    }
  }

  async function loadProgressFromServer() {
    const config = window.YNS_CONFIG || {};
    const customerId = config.customerId;
    if (!customerId) return;

    try {
      const response = await fetch(
        `${CONFIG.supabase.url}/rest/v1/${CONFIG.supabase.tableName}?customer_id=eq.${customerId}&select=*`,
        {
          headers: {
            'apikey': CONFIG.supabase.anonKey,
            'Authorization': `Bearer ${CONFIG.supabase.anonKey}`
          }
        }
      );

      if (response.ok) {
        const rows = await response.json();
        if (rows && rows.length > 0) {
          const localProgress = getProgress();

          rows.forEach(row => {
            const videoId = row.video_id;
            const serverData = {
              currentTime: row.current_time_seconds || 0,
              duration: row.duration_seconds || 0,
              progress: row.progress_percent || 0,
              watched: row.progress_percent || 0,
              completed: row.is_completed || false,
              lastWatchedAt: row.updated_at || null
            };

            const local = localProgress[videoId];

            // Merge strategy: completed always wins, otherwise keep highest progress
            if (serverData.completed || (local && local.completed)) {
              localProgress[videoId] = { ...serverData, completed: true, progress: 100, watched: 100 };
            } else if (!local || serverData.currentTime > (local.currentTime || 0)) {
              localProgress[videoId] = serverData;
            }
          });

          localStorage.setItem(CONFIG.storageKeys.videoProgress, JSON.stringify(localProgress));
          scheduleSync();
        }
      }
    } catch (e) {
      console.error('Error loading from server:', e);
    }
  }

  // ============================================
  // Stats Calculations
  // ============================================
  function calculateStats() {
    const progress = getProgress();
    let completedCount = 0;
    let totalXP = 0;
    let watchTime = 0;

    VIDEOS.forEach(video => {
      const vp = progress[video.id];
      if (vp && vp.completed) {
        completedCount++;
        totalXP += video.xp;
        const [mins] = video.duration.split(':').map(Number);
        watchTime += mins || 0;
      }
    });

    // Apply XP multiplier from streak system
    if (window.YNSStreakSystem) {
      totalXP = window.YNSStreakSystem.applyXPMultiplier(totalXP);
    }

    const completionPercent = Math.round((completedCount / VIDEOS.length) * 100);
    const currentLevel = getCurrentLevel(totalXP);
    const xpToNext = getXPToNextLevel(totalXP);

    return {
      completedCount,
      totalVideos: VIDEOS.length,
      totalXP,
      completionPercent,
      currentLevel,
      xpToNext,
      watchTimeHours: (watchTime / 60).toFixed(1)
    };
  }

  function getCurrentLevel(xp) {
    let level = LEVELS[0];
    for (const l of LEVELS) {
      if (xp >= l.xpRequired) {
        level = l;
      } else {
        break;
      }
    }
    return level;
  }

  function getXPToNextLevel(xp) {
    const current = getCurrentLevel(xp);
    const nextIndex = LEVELS.findIndex(l => l.level === current.level) + 1;
    if (nextIndex >= LEVELS.length) return 0;
    return LEVELS[nextIndex].xpRequired - xp;
  }

  // ============================================
  // UI Updates
  // ============================================
  function updateStats() {
    const stats = calculateStats();

    // Update stat elements
    const elements = {
      videosCompleted: document.getElementById('videosCompleted'),
      totalXP: document.getElementById('totalXP'),
      currentLevel: document.getElementById('currentLevel'),
      watchTime: document.getElementById('watchTime'),
      videosRemaining: document.getElementById('videosRemaining'),
      xpToNextLevel: document.getElementById('xpToNextLevel'),
      levelMessage: document.getElementById('levelMessage'),
      completionProgressBar: document.getElementById('completionProgressBar'),
      xpProgressBar: document.getElementById('xpProgressBar'),
      overallProgressRing: document.getElementById('overallProgressRing'),
      overallPercent: document.getElementById('overallPercent'),
      progressTitle: document.getElementById('progressTitle'),
      progressSubtitle: document.getElementById('progressSubtitle')
    };

    if (elements.videosCompleted) elements.videosCompleted.textContent = stats.completedCount;
    if (elements.totalXP) elements.totalXP.textContent = stats.totalXP.toLocaleString();
    if (elements.currentLevel) elements.currentLevel.textContent = `Level ${stats.currentLevel.level}`;
    if (elements.watchTime) elements.watchTime.textContent = `${stats.watchTimeHours} hrs`;
    if (elements.videosRemaining) elements.videosRemaining.textContent = `${stats.totalVideos - stats.completedCount} to finish`;
    if (elements.xpToNextLevel) elements.xpToNextLevel.textContent = stats.xpToNext > 0 ? `${stats.xpToNext} XP to next level` : 'Max level!';
    if (elements.levelMessage) elements.levelMessage.textContent = stats.currentLevel.title;

    if (elements.completionProgressBar) {
      elements.completionProgressBar.style.width = `${stats.completionPercent}%`;
    }

    if (elements.xpProgressBar) {
      const currentLevelXP = stats.currentLevel.xpRequired;
      const nextLevel = LEVELS[LEVELS.findIndex(l => l.level === stats.currentLevel.level) + 1];
      if (nextLevel) {
        const levelRange = nextLevel.xpRequired - currentLevelXP;
        const progressInLevel = stats.totalXP - currentLevelXP;
        elements.xpProgressBar.style.width = `${Math.round((progressInLevel / levelRange) * 100)}%`;
      } else {
        elements.xpProgressBar.style.width = '100%';
      }
    }

    if (elements.overallProgressRing) {
      const circumference = 2 * Math.PI * 42;
      const offset = circumference - (stats.completionPercent / 100) * circumference;
      elements.overallProgressRing.style.strokeDashoffset = offset;
    }

    if (elements.overallPercent) {
      elements.overallPercent.textContent = `${stats.completionPercent}%`;
    }

    // Update progress message
    if (elements.progressTitle && elements.progressSubtitle) {
      if (stats.completionPercent === 0) {
        elements.progressTitle.textContent = 'Get Started!';
        elements.progressSubtitle.textContent = 'Complete videos to track your progress.';
      } else if (stats.completionPercent < 25) {
        elements.progressTitle.textContent = 'Great Start!';
        elements.progressSubtitle.textContent = `You've completed ${stats.completedCount} video${stats.completedCount === 1 ? '' : 's'}. Keep going!`;
      } else if (stats.completionPercent < 50) {
        elements.progressTitle.textContent = 'Making Progress!';
        elements.progressSubtitle.textContent = `${stats.totalVideos - stats.completedCount} videos left.`;
      } else if (stats.completionPercent < 75) {
        elements.progressTitle.textContent = "You're Almost There!";
        elements.progressSubtitle.textContent = `Just ${stats.totalVideos - stats.completedCount} more videos!`;
      } else if (stats.completionPercent < 100) {
        elements.progressTitle.textContent = 'Final Stretch!';
        elements.progressSubtitle.textContent = `Only ${stats.totalVideos - stats.completedCount} videos remaining!`;
      } else {
        elements.progressTitle.textContent = 'Congratulations!';
        elements.progressSubtitle.textContent = "You've completed all videos!";
      }
    }

    updateMilestones(stats.completionPercent);
  }

  function updateMilestones(percent) {
    const milestones = [
      { id: 'milestone-start', threshold: 0 },
      { id: 'milestone-25', threshold: 25 },
      { id: 'milestone-50', threshold: 50 },
      { id: 'milestone-100', threshold: 100 }
    ];

    milestones.forEach(m => {
      const el = document.getElementById(m.id);
      if (el) {
        if (percent >= m.threshold) {
          el.classList.remove('yns-locked');
          el.classList.add('yns-completed');
        } else {
          el.classList.add('yns-locked');
          el.classList.remove('yns-completed');
        }
      }
    });
  }

  // ============================================
  // Video Rendering
  // ============================================
  function renderVideos() {
    const videoGrid = document.getElementById('videoGrid');
    if (!videoGrid) return;

    // Check video access
    const config = window.YNS_CONFIG || {};
    if (!config.access?.videos) return;

    // Filter by category
    let videos = currentCategory === 'all'
      ? [...VIDEOS]
      : VIDEOS.filter(v => v.category === currentCategory);

    // Apply sorting
    const progress = getProgress();
    switch (currentSort) {
      case 'alphabetical':
        videos.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'watched':
        videos.sort((a, b) => {
          const aCompleted = progress[a.id]?.completed ? 1 : 0;
          const bCompleted = progress[b.id]?.completed ? 1 : 0;
          return bCompleted - aCompleted || (progress[b.id]?.progress || 0) - (progress[a.id]?.progress || 0);
        });
        break;
      case 'unwatched':
        videos.sort((a, b) => {
          const aCompleted = progress[a.id]?.completed ? 1 : 0;
          const bCompleted = progress[b.id]?.completed ? 1 : 0;
          return aCompleted - bCompleted || (progress[a.id]?.progress || 0) - (progress[b.id]?.progress || 0);
        });
        break;
      default:
        videos.sort((a, b) => a.lesson - b.lesson);
    }

    videoGrid.innerHTML = videos.map(video => createVideoCard(video)).join('');

    // Add click handlers
    videoGrid.querySelectorAll('.yns-video-card').forEach(card => {
      card.addEventListener('click', () => {
        const videoId = parseInt(card.dataset.videoId, 10);
        const isLocked = card.dataset.locked === 'true';

        if (isLocked) {
          // Redirect to purchase page for locked videos
          const video = VIDEOS.find(v => v.id === videoId);
          if (video && video.purchaseUrl) {
            window.location.href = video.purchaseUrl;
          }
          return;
        }

        openVideo(videoId);
      });
    });
  }

  function createVideoCard(video) {
    const videoProgress = getVideoProgress(video.id);
    const watchedPercent = videoProgress.completed ? 100 : (videoProgress.progress || videoProgress.watched || 0);
    const completedClass = videoProgress.completed ? 'yns-completed' : '';
    const lockedClass = !video.hasAccess ? 'yns-locked' : '';
    const categoryLabel = formatCategory(video.category);

    // Only get thumbnail if we have vimeoId (user has access)
    const thumbnailUrl = video.vimeoId
      ? getVideoThumbnail(video.vimeoId)
      : 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=400&q=80';

    let resumeText = '';
    if (video.hasAccess) {
      if (!videoProgress.completed && videoProgress.currentTime > 0) {
        const mins = Math.floor(videoProgress.currentTime / 60);
        const secs = Math.floor(videoProgress.currentTime % 60);
        resumeText = `${mins}:${secs.toString().padStart(2, '0')} watched`;
      } else if (watchedPercent > 0 && !videoProgress.completed) {
        resumeText = `${Math.round(watchedPercent)}% complete`;
      }
    }

    // Locked video card
    if (!video.hasAccess) {
      return `
        <div class="yns-video-card ${lockedClass}" data-video-id="${video.id}" data-category="${video.category}" data-locked="true">
          <div class="yns-video-thumbnail">
            <img src="${thumbnailUrl}" alt="${video.title}" loading="lazy">
            <div class="yns-video-overlay yns-locked-overlay"></div>
            <div class="yns-video-lock-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
            </div>
            <span class="yns-video-duration">${video.duration}</span>
            <span class="yns-video-badge-locked">Locked</span>
          </div>
          <div class="yns-video-content">
            <div class="yns-video-lesson">Lesson ${video.lesson}</div>
            <h3 class="yns-video-title">${video.title}</h3>
            <div class="yns-video-meta">
              <span class="yns-video-category">${categoryLabel}</span>
            </div>
            <a href="${video.purchaseUrl}" class="yns-btn-unlock-video" onclick="event.stopPropagation();">
              Unlock this lecture
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </a>
          </div>
        </div>
      `;
    }

    // Unlocked video card (has access)
    return `
      <div class="yns-video-card ${completedClass}" data-video-id="${video.id}" data-category="${video.category}">
        <div class="yns-video-thumbnail">
          <img src="${thumbnailUrl}" alt="${video.title}" loading="lazy" onerror="this.src='https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=400&q=80'">
          <div class="yns-video-overlay"></div>
          <div class="yns-video-play-btn">
            <svg viewBox="0 0 24 24"><polygon points="5,3 19,12 5,21"/></svg>
          </div>
          <span class="yns-video-duration">${video.duration}</span>
          ${videoProgress.completed ? '<span class="yns-completed-badge">Done</span>' : ''}
          <span class="yns-video-xp-badge">+${video.xp} XP</span>
          ${watchedPercent > 0 ? `<div class="yns-video-progress-bar"><div class="yns-video-progress-fill" style="width: ${watchedPercent}%"></div></div>` : ''}
        </div>
        <div class="yns-video-content">
          <div class="yns-video-lesson">Lesson ${video.lesson}</div>
          <h3 class="yns-video-title">${video.title}</h3>
          <div class="yns-video-meta">
            <span class="yns-video-category">${categoryLabel}</span>
            ${resumeText ? `<span>${resumeText}</span>` : ''}
          </div>
        </div>
      </div>
    `;
  }

  function getVideoThumbnail(vimeoId) {
    // If no vimeoId (user doesn't have access), return placeholder
    if (!vimeoId) {
      return 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=400&q=80';
    }

    try {
      const cache = JSON.parse(localStorage.getItem(CONFIG.storageKeys.thumbnailCache) || '{}');
      return cache[vimeoId] || `https://vumbnail.com/${vimeoId}.jpg`;
    } catch (e) {
      return `https://vumbnail.com/${vimeoId}.jpg`;
    }
  }

  function formatCategory(category) {
    return category.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  }

  // ============================================
  // Featured Video (Secure)
  // ============================================
  function updateFeaturedVideo() {
    // Only show videos user has access to
    const accessibleVideos = VIDEOS.filter(v => v.hasAccess && v.vimeoId);

    if (accessibleVideos.length === 0) {
      // Hide featured section if no accessible videos
      const featuredCard = document.querySelector('.yns-card-featured');
      if (featuredCard) featuredCard.style.display = 'none';
      return;
    }

    const allProgress = getProgress();

    // Find a video in progress (only from accessible videos)
    let featuredVideo = accessibleVideos.find(v => {
      const vp = allProgress[v.id];
      return vp && (vp.progress > 0 || vp.currentTime > 0) && !vp.completed;
    });

    if (!featuredVideo) {
      const inProgressVideos = accessibleVideos.filter(v => {
        const vp = allProgress[v.id];
        return vp && !vp.completed && vp.lastWatchedAt;
      }).sort((a, b) => {
        const aTime = allProgress[a.id]?.lastWatchedAt || '';
        const bTime = allProgress[b.id]?.lastWatchedAt || '';
        return bTime.localeCompare(aTime);
      });

      featuredVideo = inProgressVideos[0] || accessibleVideos[0];
    }

    const featuredProgress = getVideoProgress(featuredVideo.id);
    const watchedPercent = featuredProgress.progress || featuredProgress.watched || 0;

    const elements = {
      image: document.getElementById('featuredImage'),
      title: document.getElementById('featuredTitle'),
      category: document.getElementById('featuredCategory'),
      duration: document.getElementById('featuredDuration'),
      progressBar: document.getElementById('featuredProgressBar'),
      progressText: document.getElementById('featuredProgressText'),
      thumbnail: document.getElementById('featuredThumbnail'),
      playBtn: document.getElementById('featuredPlayBtn')
    };

    if (elements.image) {
      elements.image.src = getVideoThumbnail(featuredVideo.vimeoId);
      elements.image.alt = featuredVideo.title;
    }

    if (elements.title) elements.title.textContent = featuredVideo.title;
    if (elements.category) elements.category.textContent = `📚 ${formatCategory(featuredVideo.category)}`;
    if (elements.duration) elements.duration.textContent = `⏱️ ${featuredVideo.duration}`;
    if (elements.progressBar) elements.progressBar.style.width = `${watchedPercent}%`;

    if (elements.progressText) {
      if (featuredProgress.completed) {
        elements.progressText.innerHTML = '<strong>Completed</strong>';
      } else if (featuredProgress.currentTime > 0) {
        const mins = Math.floor(featuredProgress.currentTime / 60);
        const secs = Math.floor(featuredProgress.currentTime % 60);
        elements.progressText.innerHTML = `<strong>${mins}:${secs.toString().padStart(2, '0')}</strong> watched`;
      } else if (watchedPercent > 0) {
        elements.progressText.innerHTML = `<strong>${Math.round(watchedPercent)}%</strong> complete`;
      } else {
        elements.progressText.innerHTML = 'Not started';
      }
    }

    if (elements.playBtn) {
      const btnText = featuredProgress.currentTime > 0 || watchedPercent > 0 ? 'Resume Learning' : 'Start Learning';
      elements.playBtn.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><polygon points="5,3 19,12 5,21"/></svg> ${btnText}`;
    }

    if (elements.thumbnail) {
      elements.thumbnail.onclick = () => openVideo(featuredVideo.id);
    }
    if (elements.playBtn) {
      elements.playBtn.onclick = () => openVideo(featuredVideo.id);
    }
  }

  // ============================================
  // Video Modal (Secure)
  // ============================================
  function openVideo(videoId) {
    const video = VIDEOS.find(v => v.id === videoId);
    if (!video) return;

    // Security check: Verify user has access (vimeoId is present)
    if (!video.hasAccess || !video.vimeoId) {
      console.warn('No access to video:', videoId);
      if (video.purchaseUrl) {
        window.location.href = video.purchaseUrl;
      }
      return;
    }

    currentVideoIndex = VIDEOS.indexOf(video);
    currentVideoId = videoId;

    const modal = document.getElementById('videoModal');
    const modalVideo = document.getElementById('modalVideo');
    const modalTitle = document.getElementById('modalTitle');
    const modalLesson = document.getElementById('modalLesson');
    const modalDuration = document.getElementById('modalDuration');
    const modalCategory = document.getElementById('modalCategory');
    const markCompleteBtn = document.getElementById('markCompleteBtn');

    if (modal) modal.classList.add('active');

    // Use the secure vimeoId from server-rendered data
    if (modalVideo) {
      modalVideo.innerHTML = `
        <iframe
          id="vimeoPlayer"
          src="https://player.vimeo.com/video/${video.vimeoId}?${video.vimeoHash ? `h=${video.vimeoHash}&` : ''}autoplay=1&title=0&byline=0&portrait=0"
          allow="autoplay; fullscreen; picture-in-picture"
          allowfullscreen>
        </iframe>
      `;
    }

    if (modalTitle) modalTitle.textContent = video.title;
    if (modalLesson) modalLesson.textContent = `Lesson ${video.lesson}`;
    if (modalDuration) modalDuration.textContent = video.duration;
    if (modalCategory) modalCategory.textContent = formatCategory(video.category);

    const progress = getVideoProgress(videoId);
    if (markCompleteBtn) {
      markCompleteBtn.textContent = progress.completed ? 'Completed' : 'Mark Complete';
    }

    document.body.style.overflow = 'hidden';

    // Initialize Vimeo player
    initVimeoPlayer(videoId, video);

    // Record activity
    if (window.YNSStreakSystem) {
      window.YNSStreakSystem.recordActivity('video_watched', {
        videoId: video.id,
        videoTitle: video.title
      });
    }
  }

  function initVimeoPlayer(videoId, video) {
    let attempts = 0;
    const maxAttempts = 20;

    function tryInit() {
      attempts++;
      const iframe = document.getElementById('vimeoPlayer');

      if (!iframe || typeof Vimeo === 'undefined') {
        if (attempts < maxAttempts) {
          setTimeout(tryInit, 200);
        }
        return;
      }

      try {
        currentPlayer = new Vimeo.Player(iframe);
        lastSaveTime = 0;

        const savedProgress = getVideoProgress(videoId);

        if (!savedProgress.completed && savedProgress.currentTime > 0 && savedProgress.progress < 95) {
          const resumeTime = Math.max(0, savedProgress.currentTime - 5);
          currentPlayer.setCurrentTime(resumeTime);
        }

        currentPlayer.getDuration().then(duration => {
          const progress = getProgress();
          progress[videoId] = { ...getVideoProgress(videoId), duration: Math.floor(duration) };
          saveProgress(progress);
        });

        currentPlayer.on('timeupdate', (data) => {
          const currentTime = Math.floor(data.seconds);
          const duration = Math.floor(data.duration);
          const progressPercent = Math.round((currentTime / duration) * 100);

          // Save locally
          const progress = getProgress();
          progress[videoId] = {
            ...getVideoProgress(videoId),
            currentTime,
            duration,
            progress: progressPercent,
            watched: progressPercent,
            lastWatchedAt: new Date().toISOString()
          };
          localStorage.setItem(CONFIG.storageKeys.videoProgress, JSON.stringify(progress));

          // Sync periodically
          if (currentTime - lastSaveTime >= 30) {
            lastSaveTime = currentTime;
            scheduleSync();
          }
        });

        currentPlayer.on('pause', () => {
          scheduleSync();
        });

        currentPlayer.on('ended', () => {
          const savedProgress = getVideoProgress(videoId);
          if (!savedProgress.completed) {
            updateVideoProgress(videoId, {
              completed: true,
              progress: 100,
              watched: 100
            });
            closeVideo();
            showCelebration(video.xp);
            updateFeaturedVideo();

            // Record completion
            if (window.YNSStreakSystem) {
              window.YNSStreakSystem.recordActivity('video_completed', {
                videoId: video.id,
                videoTitle: video.title,
                xp: video.xp
              });
            }
          }
        });

      } catch (e) {
        console.error('Error initializing player:', e);
      }
    }

    setTimeout(tryInit, 100);
  }

  function closeVideo() {
    if (currentPlayer && currentVideoId) {
      currentPlayer.getCurrentTime().then(time => {
        currentPlayer.getDuration().then(duration => {
          const progressPercent = Math.round((time / duration) * 100);
          updateVideoProgress(currentVideoId, {
            currentTime: Math.floor(time),
            duration: Math.floor(duration),
            progress: progressPercent
          });
        }).catch(() => {});
      }).catch(() => {});

      try {
        currentPlayer.destroy();
      } catch (e) {}
    }

    currentPlayer = null;

    const modal = document.getElementById('videoModal');
    const modalVideo = document.getElementById('modalVideo');

    if (modal) modal.classList.remove('active');
    if (modalVideo) modalVideo.innerHTML = '';

    currentVideoId = null;
    document.body.style.overflow = '';

    renderVideos();
    updateFeaturedVideo();
  }

  function showCelebration(xp) {
    const modal = document.getElementById('celebrationModal');
    const celebrationXP = document.getElementById('celebrationXP');

    if (celebrationXP) {
      const multiplier = window.YNSStreakSystem ? window.YNSStreakSystem.getXPMultiplier() : 1;
      const totalXP = Math.round(xp * multiplier);
      celebrationXP.innerHTML = `<span>+${totalXP} XP Earned</span><span>⭐</span>`;
    }

    if (modal) modal.classList.add('active');
    createConfetti();
  }

  function closeCelebration() {
    const modal = document.getElementById('celebrationModal');
    if (modal) modal.classList.remove('active');
  }

  function createConfetti() {
    const container = document.getElementById('confettiContainer');
    if (!container) return;

    const colors = ['#46523E', '#5A6B4D', '#C4785A', '#B8976A', '#5A8A5A'];

    for (let i = 0; i < 50; i++) {
      const confetti = document.createElement('div');
      confetti.className = 'yns-confetti';
      confetti.style.left = Math.random() * 100 + 'vw';
      confetti.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
      confetti.style.width = (Math.random() * 8 + 4) + 'px';
      confetti.style.height = confetti.style.width;
      confetti.style.borderRadius = Math.random() > 0.5 ? '50%' : '0';
      confetti.style.animation = `yns-confetti-fall ${Math.random() * 3 + 2}s linear forwards`;
      confetti.style.animationDelay = Math.random() * 0.5 + 's';

      container.appendChild(confetti);
      setTimeout(() => confetti.remove(), 5000);
    }
  }

  // ============================================
  // Event Handlers
  // ============================================
  function setupEventHandlers() {
    // Category filters
    const categoryPills = document.getElementById('categoryPills');
    if (categoryPills) {
      categoryPills.addEventListener('click', (e) => {
        const pill = e.target.closest('.yns-category-pill');
        if (!pill) return;

        categoryPills.querySelectorAll('.yns-category-pill').forEach(p => p.classList.remove('active'));
        pill.classList.add('active');

        currentCategory = pill.dataset.category;
        renderVideos();
      });
    }

    // Sort dropdown
    const sortBtn = document.getElementById('sortBtn');
    const sortMenu = document.getElementById('sortMenu');
    const sortLabel = document.getElementById('sortLabel');

    if (sortBtn && sortMenu) {
      sortBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        sortMenu.classList.toggle('active');
        sortBtn.classList.toggle('active');
      });

      sortMenu.addEventListener('click', (e) => {
        const option = e.target.closest('.yns-sort-option');
        if (!option) return;

        sortMenu.querySelectorAll('.yns-sort-option').forEach(o => o.classList.remove('active'));
        option.classList.add('active');

        currentSort = option.dataset.sort;
        if (sortLabel) sortLabel.textContent = option.textContent;

        sortMenu.classList.remove('active');
        sortBtn.classList.remove('active');
        renderVideos();
      });

      document.addEventListener('click', (e) => {
        if (!e.target.closest('.yns-sort-dropdown')) {
          sortMenu.classList.remove('active');
          sortBtn.classList.remove('active');
        }
      });
    }

    // Video modal
    document.getElementById('modalCloseBtn')?.addEventListener('click', closeVideo);
    document.getElementById('prevBtn')?.addEventListener('click', () => {
      if (currentVideoIndex > 0) openVideo(VIDEOS[currentVideoIndex - 1].id);
    });
    document.getElementById('nextBtn')?.addEventListener('click', () => {
      if (currentVideoIndex < VIDEOS.length - 1) openVideo(VIDEOS[currentVideoIndex + 1].id);
    });
    document.getElementById('markCompleteBtn')?.addEventListener('click', () => {
      const video = VIDEOS[currentVideoIndex];
      if (!video) return;

      const savedProgress = getVideoProgress(video.id);
      const newCompleted = !savedProgress.completed;

      updateVideoProgress(video.id, {
        completed: newCompleted,
        progress: newCompleted ? 100 : savedProgress.progress
      });

      const btn = document.getElementById('markCompleteBtn');
      if (btn) btn.textContent = newCompleted ? '✓ Completed' : '✓ Mark Complete';

      if (newCompleted) {
        closeVideo();
        showCelebration(video.xp);
      }

      updateFeaturedVideo();
    });

    // Celebration modal
    document.getElementById('celebrationContinueBtn')?.addEventListener('click', closeCelebration);
    document.getElementById('celebrationModal')?.addEventListener('click', (e) => {
      if (e.target.classList.contains('yns-celebration-modal')) closeCelebration();
    });

    // Video modal backdrop
    document.getElementById('videoModal')?.addEventListener('click', (e) => {
      if (e.target.classList.contains('yns-modal')) closeVideo();
    });

    // Keyboard shortcuts
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        closeVideo();
        closeCelebration();
      }
    });

    // Save on page unload
    window.addEventListener('beforeunload', () => {
      if (currentPlayer && currentVideoId) {
        const progress = getProgress();
        localStorage.setItem(CONFIG.storageKeys.videoProgress, JSON.stringify(progress));
      }
    });
  }

  // ============================================
  // Thumbnail Preloading (Secure)
  // ============================================
  async function preloadThumbnails() {
    try {
      const cache = JSON.parse(localStorage.getItem(CONFIG.storageKeys.thumbnailCache) || '{}');

      // Only preload thumbnails for videos user has access to (have vimeoId)
      const accessibleVideos = VIDEOS.filter(v => v.hasAccess && v.vimeoId);
      const uncached = accessibleVideos.filter(v => !cache[v.vimeoId]);

      if (uncached.length === 0) return;

      for (let i = 0; i < uncached.length; i += 5) {
        const batch = uncached.slice(i, i + 5);
        await Promise.all(batch.map(async v => {
          try {
            const response = await fetch(`https://vimeo.com/api/oembed.json?url=https://vimeo.com/${v.vimeoId}`);
            if (response.ok) {
              const data = await response.json();
              let thumbnailUrl = data.thumbnail_url?.replace(/_\d+x\d+/, '_640x360');
              cache[v.vimeoId] = thumbnailUrl;
            }
          } catch (e) {}
        }));

        if (i + 5 < uncached.length) {
          await new Promise(r => setTimeout(r, 200));
        }
      }

      localStorage.setItem(CONFIG.storageKeys.thumbnailCache, JSON.stringify(cache));
      renderVideos();
      updateFeaturedVideo();
    } catch (e) {
      console.error('Thumbnail preload error:', e);
    }
  }

  // ============================================
  // Initialization
  // ============================================
  async function init() {
    // Initialize videos from secure server-rendered data
    initVideosFromSecureData();

    // Load progress from server
    await loadProgressFromServer();

    // Setup UI
    setupEventHandlers();

    // Check if we have video access from secure data
    const secureData = window.YNS_SECURE_DATA || {};
    const hasVideoAccess = secureData.access?.videos || false;

    // Fallback to YNS_CONFIG for backwards compatibility
    const config = window.YNS_CONFIG || {};
    const hasAccessFromConfig = config.access?.videos || false;

    if (hasVideoAccess || hasAccessFromConfig) {
      renderVideos();
      updateStats();
      updateFeaturedVideo();

      // Preload thumbnails in background (only for accessible videos)
      preloadThumbnails();
    }
  }

  // ============================================
  // Public API
  // ============================================
  window.YNSEducationHub = {
    init,
    openVideo,
    closeVideo,
    renderVideos,
    updateStats,
    getProgress,
    getVideoProgress,
    scheduleSync,
    hasVideoAccess,
    getSecureVimeoId,
    getVideos: () => VIDEOS,
    LEVELS
  };

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
